/**
 * HomeBanner — full-width landscape banner with text overlaid on the image
 * and a dark gradient at the bottom for legibility.
 *
 * When multiple banners are live, they auto-slide right→left seamlessly
 * (duplicate-DOM CSS marquee, no controls, no user interaction).
 */
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Megaphone } from "lucide-react";
import ResponsiveImage from "./ResponsiveImage";

const GAP_PX = 14;
const PX_PER_SECOND = 70;  // marquee speed

const resolveUrl = (u) =>
    u && u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u || ""}`;

function BannerTile({ banner, widthClass = "w-full", testidSuffix = "", eager = false }) {
    const hasImage = !!banner.image_url;
    return (
        <div
            data-testid={`home-banner-tile${testidSuffix}`}
            className={`relative ${widthClass} h-full rounded-lg overflow-hidden bg-slate-900 border border-slate-800 flex-shrink-0`}
        >
            {hasImage ? (
                <ResponsiveImage
                    src={banner.image_url}
                    alt={banner.title || ""}
                    eager={eager}
                    testid="home-banner-image"
                    className="absolute inset-0 w-full h-full object-cover object-center select-none"
                />
            ) : (
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a3a1f] via-slate-900 to-slate-950 flex items-center justify-center">
                    <Megaphone className="w-10 h-10 text-[#00FF66]/40" />
                </div>
            )}
            {/* Bottom-to-top dark gradient for text legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent pointer-events-none" />
            {/* Bottom-left text overlay — clamped inside the visible safe area */}
            <div className="absolute left-4 right-4 bottom-3 sm:left-5 sm:bottom-4 max-w-[min(680px,90%)]">
                <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#00FF66]">Announcement</div>
                <h3
                    data-testid="home-banner-title"
                    className="font-display text-sm sm:text-base md:text-lg font-bold uppercase text-white truncate"
                >
                    {banner.title}
                </h3>
                <p
                    data-testid="home-banner-message"
                    className="text-[11px] sm:text-xs text-slate-200/95 line-clamp-2 mt-0.5"
                >
                    {banner.message}
                </p>
            </div>
        </div>
    );
}

export default function HomeBanner() {
    const [banners, setBanners] = useState([]);

    useEffect(() => {
        let cancelled = false;
        api.get("/banners/active-list")
            .then(({ data }) => { if (!cancelled) setBanners(Array.isArray(data) ? data : []); })
            .catch(() => { /* silent */ });
        return () => { cancelled = true; };
    }, []);

    const multiple = banners.length > 1;

    // Marquee duration scales with how many tiles are visible.
    const durationSec = useMemo(() => {
        if (!multiple) return 0;
        const approxTileWidth = 800;
        const total = (approxTileWidth + GAP_PX) * banners.length;
        return Math.max(12, Math.round(total / PX_PER_SECOND));
    }, [multiple, banners.length]);

    if (banners.length === 0) return null;

    // Responsive heights: 180px mobile / 220px tablet / 280px desktop
    const heightClasses = "h-[180px] sm:h-[220px] lg:h-[280px]";

    if (!multiple) {
        return (
            <div
                className={`mb-5 w-full ${heightClasses}`}
                data-testid="home-banner"
            >
                <BannerTile banner={banners[0]} testidSuffix="-0" eager />
            </div>
        );
    }

    // Multiple → marquee with overlay tiles. Duplicate DOM for seamless loop.
    return (
        <div
            data-testid="home-banner"
            className={`relative mb-5 w-full overflow-hidden ${heightClasses}`}
        >
            <div
                className="flex items-stretch h-full home-banner-track"
                style={{ animationDuration: `${durationSec}s`, gap: `${GAP_PX}px` }}
            >
                {[...banners, ...banners].map((b, i) => (
                    <div
                        key={`${b.id}-${i}`}
                        className="h-full flex-shrink-0"
                        style={{ width: "min(800px, 92vw)" }}
                    >
                        <BannerTile banner={b} testidSuffix={`-${i}`} eager={i === 0} />
                    </div>
                ))}
            </div>
            <style>{`
                .home-banner-track {
                    width: max-content;
                    animation-name: home-banner-scroll;
                    animation-timing-function: linear;
                    animation-iteration-count: infinite;
                }
                @keyframes home-banner-scroll {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .home-banner-track { animation: none; }
                }
            `}</style>
        </div>
    );
}
